import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

interface HeroData {
  title: string;
  subtitle: string;
  badge: string;
  btn1_text: string;
  btn1_link: string;
  btn2_text: string;
  btn2_link: string;
  image_url: string;
  overlay_opacity: number;
}

const defaultHero: HeroData = {
  title: 'A elegância que reside no Uísque Escocês',
  subtitle: 'Whiskies e gins selecionados das três grandes regiões escocesas — Islay, Speyside e Highlands.',
  badge: '',
  btn1_text: 'Comprar Agora',
  btn1_link: '/loja',
  btn2_text: 'Ver Whiskies',
  btn2_link: '/whiskies',
  image_url: 'https://readdy.ai/api/search-image?query=dramatic%20dark%20whisky%20distillery%20interior%20copper%20pot%20stills%20golden%20amber%20light%20rays%20atmospheric%20moody%20photography%20premium%20spirits%20luxury%20Scottish%20distillery%20barrels%20aged%20oak%20cinematic&width=1600&height=900&seq=hero-main&orientation=landscape',
  overlay_opacity: 60,
};

export default function AdminHeroPage() {
  const [data, setData] = useState<HeroData>(defaultHero);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadHero();
  }, []);

  const loadHero = async () => {
    const { data: row } = await supabase
      .from('site_sections')
      .select('*')
      .eq('page', 'home')
      .eq('section_key', 'hero')
      .maybeSingle();

    if (row) {
      setData({
        title: row.title || defaultHero.title,
        subtitle: row.subtitle || defaultHero.subtitle,
        badge: row.body || defaultHero.badge,
        btn1_text: row.button_text || defaultHero.btn1_text,
        btn1_link: row.button_link || defaultHero.btn1_link,
        btn2_text: row.extra_data?.btn2_text || defaultHero.btn2_text,
        btn2_link: row.extra_data?.btn2_link || defaultHero.btn2_link,
        image_url: row.image_url || defaultHero.image_url,
        overlay_opacity: row.extra_data?.overlay_opacity ?? defaultHero.overlay_opacity,
      });
    }
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    const payload = {
      page: 'home',
      section_key: 'hero',
      title: data.title,
      subtitle: data.subtitle,
      body: data.badge,
      button_text: data.btn1_text,
      button_link: data.btn1_link,
      image_url: data.image_url,
      extra_data: {
        btn2_text: data.btn2_text,
        btn2_link: data.btn2_link,
        overlay_opacity: data.overlay_opacity,
      },
      is_active: true,
      updated_at: new Date().toISOString(),
    };

    const { data: existing } = await supabase
      .from('site_sections')
      .select('id')
      .eq('page', 'home')
      .eq('section_key', 'hero')
      .maybeSingle();

    if (existing) {
      await supabase.from('site_sections').update(payload).eq('id', existing.id);
    } else {
      await supabase.from('site_sections').insert(payload);
    }

    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div>
      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5">{label}</label>
      {children}
    </div>
  );

  const inputCls = "w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-[#C9A84C] transition-colors";

  if (loading) return <div className="flex items-center justify-center h-40"><i className="ri-loader-4-line text-[#C9A84C] text-2xl animate-spin"></i></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-800">Gestão do Hero</h1>
          <p className="text-gray-400 text-sm mt-0.5">Edite a seção principal da página inicial</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#C9A84C] hover:bg-[#D4AF37] text-black text-sm font-semibold px-5 py-2.5 rounded-lg whitespace-nowrap cursor-pointer transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          {saving ? <><i className="ri-loader-4-line animate-spin"></i> Salvando...</> : saved ? <><i className="ri-check-line"></i> Salvo!</> : <><i className="ri-save-line"></i> Salvar</>}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: form */}
        <div className="space-y-5">
          <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-4">
            <h2 className="text-sm font-semibold text-gray-700 border-b border-gray-100 pb-3">Textos</h2>
            <Field label="Badge / Eyebrow">
              <input className={inputCls} value={data.badge} onChange={e => setData({ ...data, badge: e.target.value })} placeholder="Ex: Destilados Premium" />
            </Field>
            <Field label="Título Principal">
              <textarea className={inputCls} rows={3} value={data.title} onChange={e => setData({ ...data, title: e.target.value })} />
            </Field>
            <Field label="Subtítulo">
              <textarea className={inputCls} rows={3} value={data.subtitle} onChange={e => setData({ ...data, subtitle: e.target.value })} />
            </Field>
          </div>

          <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-4">
            <h2 className="text-sm font-semibold text-gray-700 border-b border-gray-100 pb-3">Botões</h2>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Botão 1 — Texto">
                <input className={inputCls} value={data.btn1_text} onChange={e => setData({ ...data, btn1_text: e.target.value })} />
              </Field>
              <Field label="Botão 1 — Link">
                <input className={inputCls} value={data.btn1_link} onChange={e => setData({ ...data, btn1_link: e.target.value })} />
              </Field>
              <Field label="Botão 2 — Texto">
                <input className={inputCls} value={data.btn2_text} onChange={e => setData({ ...data, btn2_text: e.target.value })} />
              </Field>
              <Field label="Botão 2 — Link">
                <input className={inputCls} value={data.btn2_link} onChange={e => setData({ ...data, btn2_link: e.target.value })} />
              </Field>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-4">
            <h2 className="text-sm font-semibold text-gray-700 border-b border-gray-100 pb-3">Imagem de Fundo</h2>
            <Field label="URL da Imagem">
              <input className={inputCls} value={data.image_url} onChange={e => setData({ ...data, image_url: e.target.value })} placeholder="https://..." />
            </Field>
            <Field label={`Opacidade do Overlay Escuro: ${data.overlay_opacity}%`}>
              <input
                type="range" min={0} max={90} step={5}
                value={data.overlay_opacity}
                onChange={e => setData({ ...data, overlay_opacity: Number(e.target.value) })}
                className="w-full accent-[#C9A84C]"
              />
            </Field>
          </div>
        </div>

        {/* Right: preview */}
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h2 className="text-sm font-semibold text-gray-700 border-b border-gray-100 pb-3 mb-4">Pré-visualização</h2>
          <div className="relative rounded-lg overflow-hidden h-64">
            <img src={data.image_url} alt="Hero preview" className="w-full h-full object-cover object-top" />
            <div className="absolute inset-0" style={{ background: `rgba(0,0,0,${data.overlay_opacity / 100})` }} />
            <div className="absolute inset-0 flex flex-col justify-center px-6">
              {data.badge && <p className="text-[#C9A84C] text-[10px] tracking-widest uppercase mb-2">{data.badge}</p>}
              <h3 className="text-white text-lg font-light leading-tight mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{data.title}</h3>
              <p className="text-white/60 text-xs mb-4 line-clamp-2">{data.subtitle}</p>
              <div className="flex gap-2">
                <span className="bg-[#C9A84C] text-black text-[10px] font-bold px-3 py-1.5 rounded">{data.btn1_text}</span>
                <span className="border border-white/40 text-white text-[10px] px-3 py-1.5 rounded">{data.btn2_text}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
